import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Eye, EyeOff, Mail, Wand2, CheckCircle2, ArrowLeft, QrCode, Smartphone } from "lucide-react";
import GoogleAuthButton from "../auth/GoogleAuthButton";
import AppleAuthButton from "../auth/AppleAuthButton";
import XAuthButton from "../auth/XAuthButton";
import SMSSignInForm from "../auth/SMSSignInForm";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import PasswordStrengthInput from "./PasswordStrengthInput";
import { validatePassword } from "../../lib/passwordRules";

// AuthForm has 3 modes when isLogin=true:
//   "magic"        — primary, default. Enter email → get one-tap link.
//   "magic-sent"   — success screen after magic-link request submit.
//   "password"     — fallback for users who prefer typing their password.
//   "sms"          — phone-number sign-in via Twilio Verify (Phase 4).
// Register flow is unchanged (username + email + strong password).
const AuthForm = ({ isLogin, onSuccess }) => {
  const { login, register, requestMagicLink } = useAuth();
  // Pre-fill email from localStorage so returning users (especially on
  // mobile) don't have to retype their address every visit. We only
  // store the email (not password), and we update it on each successful
  // submission below. Falls back to empty if storage is unavailable.
  const [email, setEmail] = useState(() => {
    try { return localStorage.getItem("ge:lastEmail") || ""; } catch (_) { return ""; }
  });
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  // Home-city state (register flow only). On mount we try a single
  // best-effort IP-geo lookup via OUR backend proxy `/api/geo/ip-lookup`
  // (server-side hits ip-api.com so the browser never makes a third-
  // party request — eliminates the recurring CORS console error from
  // ipapi.co). User can always overwrite. We never block submit on
  // this so a slow / blocked lookup degrades to a manual-entry form.
  const [homeCity, setHomeCity] = useState("");
  const [homeCityCountry, setHomeCityCountry] = useState("");
  const [homeCityLat, setHomeCityLat] = useState(null);
  const [homeCityLng, setHomeCityLng] = useState(null);
  React.useEffect(() => {
    if (isLogin) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL;
        const r = await fetch(`${API}/api/geo/ip-lookup`, { cache: "no-store" });
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled) return;
        if (data?.city) setHomeCity(data.city);
        if (data?.country_name) setHomeCityCountry(data.country_name);
        if (typeof data?.latitude === "number") setHomeCityLat(data.latitude);
        if (typeof data?.longitude === "number") setHomeCityLng(data.longitude);
      } catch (_) { /* silent best-effort */ }
    })();
    return () => { cancelled = true; };
  }, [isLogin]);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("magic"); // 'magic' | 'magic-sent' | 'password'

  // Math-captcha state (register-only). Self-hosted, no third-party
  // tracker. Server issues a small arithmetic question, user types
  // the answer, server marks the challenge "passed", we forward the
  // challenge_id to /auth/register which consumes it. Reduces drive-
  // by signup-bonus farming under the 5/hr/IP rate limit.
  const [captchaId, setCaptchaId] = useState("");
  const [captchaQuestion, setCaptchaQuestion] = useState("");
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [captchaError, setCaptchaError] = useState("");
  React.useEffect(() => {
    if (isLogin) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL;
        const r = await fetch(`${API}/api/captcha/challenge`, { cache: "no-store" });
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled) return;
        setCaptchaId(data.challenge_id);
        setCaptchaQuestion(data.question);
        setCaptchaAnswer("");
        setCaptchaVerified(false);
      } catch (_) { /* silent — register will 400 with a clear msg */ }
    })();
    return () => { cancelled = true; };
  }, [isLogin]);

  const verifyCaptcha = async () => {
    setCaptchaError("");
    if (!captchaId) return false;
    const ans = parseInt(captchaAnswer, 10);
    if (Number.isNaN(ans)) {
      setCaptchaError("Enter a number.");
      return false;
    }
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const r = await fetch(`${API}/api/captcha/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ challenge_id: captchaId, answer: ans }),
      });
      if (r.ok) {
        setCaptchaVerified(true);
        return true;
      }
      const body = await r.json().catch(() => ({}));
      setCaptchaError(body.detail || "Wrong answer. Try again.");
      return false;
    } catch {
      setCaptchaError("Captcha service unavailable. Try again.");
      return false;
    }
  };

  // Listen for the global "close overlays" event (fired when the user
  // taps the header logo) so the magic-sent success screen also clears
  // and the user lands cleanly on home. Top-level useEffect to honour
  // rules-of-hooks regardless of which render branch fires below.
  React.useEffect(() => {
    if (mode !== "magic-sent") return undefined;
    const handler = () => { setMode("magic"); setEmail(""); setError(""); };
    window.addEventListener('globaleyes:close-overlays', handler);
    return () => window.removeEventListener('globaleyes:close-overlays', handler);
  }, [mode]);

  const normalizedEmail = () => (email || "").trim().toLowerCase();

  const handleMagicSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !email.includes("@")) {
      setError("Enter the email you signed up with.");
      return;
    }
    setLoading(true);
    // Fail-open UX: the backend uses asyncio.create_task to fire the
    // email AFTER returning, so even if our HTTP request times out on a
    // flaky cellular link, the email almost always lands. Showing the
    // "Check your inbox" screen optimistically (and surfacing rate-limit
    // errors specifically) is the same model the /forgot-password page
    // uses and is the universal pattern (Vercel, Notion, Substack).
    try {
      await requestMagicLink(normalizedEmail());
      try { localStorage.setItem("ge:lastEmail", normalizedEmail()); } catch (_) { /* noop */ }
      setMode("magic-sent");
    } catch (err) {
      if (err.response?.status === 429) {
        setError(err.response?.data?.detail || "Too many sign-in attempts. Try again in a few minutes.");
      } else {
        // Network blip / timeout / anything else — assume the email
        // pipeline ran anyway (backend fires-and-forgets) and show
        // success. If it truly didn't send, the user retries from the
        // success screen's "Use a different email" path.
        try { localStorage.setItem("ge:lastEmail", normalizedEmail()); } catch (_) { /* noop */ }
        setMode("magic-sent");
      }
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    // Register: enforce strong-password rules client-side first so the
    // user sees the exact rule that failed instead of a generic API error.
    if (!isLogin) {
      const pwError = validatePassword(password);
      if (pwError) {
        setError(pwError);
        return;
      }
      if (!homeCity.trim()) {
        setError("Home city is required — we use it to drop 3 starter video commissions in your area.");
        return;
      }
      // Captcha must be verified before the register request goes out.
      // Lazy-verify here (if the user clicked Submit before the inline
      // verify button) so the UX is "type answer + Submit" in one shot.
      if (!captchaVerified) {
        const ok = await verifyCaptcha();
        if (!ok) {
          setError("Please solve the math challenge below.");
          return;
        }
      }
    }
    setLoading(true);
    try {
      if (isLogin) {
        await login(normalizedEmail(), password);
        toast.success("Welcome back!");
      } else {
        await register(username, normalizedEmail(), password, {
          home_city: homeCity.trim(),
          home_city_country: homeCityCountry.trim() || undefined,
          home_city_lat: homeCityLat ?? undefined,
          home_city_lng: homeCityLng ?? undefined,
          captcha_token: captchaId,
        });
        toast.success("Welcome to GlobalEyes! +20 cr landed and 3 commissions are coming to your inbox.");
      }
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || "An error occurred");
    }
    setLoading(false);
  };

  // ----- Register screen — unchanged from before, just consolidated here.
  if (!isLogin) {
    return (
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form">
        <div>
          <Label htmlFor="username" className="text-gray-300">Username</Label>
          <Input id="username" value={username} onChange={(e) => setUsername(e.target.value)}
            className="bg-gray-800 border-gray-700 text-white mt-1" placeholder="cooluser123"
            required
            autoCapitalize="none" autoCorrect="off" spellCheck={false}
            data-testid="username-input" />
        </div>
        <div>
          <Label htmlFor="email" className="text-gray-300">Email</Label>
          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            className="bg-gray-800 border-gray-700 text-white mt-1" placeholder="you@example.com"
            required
            autoCapitalize="none" autoCorrect="off" spellCheck={false} inputMode="email"
            data-testid="email-input" />
        </div>
        <div>
          <Label htmlFor="password" className="text-gray-300">Password</Label>
          <div className="mt-1">
            <PasswordStrengthInput
              value={password}
              onChange={setPassword}
              placeholder="Create a strong password"
              testIdBase="register-password"
            />
          </div>
        </div>
        <div>
          <Label htmlFor="home-city" className="text-gray-300">Your home city <span className="text-amber-400">*</span></Label>
          <Input id="home-city" value={homeCity} onChange={(e) => setHomeCity(e.target.value)}
            className="bg-gray-800 border-gray-700 text-white mt-1" placeholder="e.g. Brooklyn, NY"
            required autoCapitalize="words" autoCorrect="off" spellCheck={false}
            data-testid="home-city-input" />
          <p className="text-gray-500 text-[11px] mt-1">
            We auto-detect from your network — feel free to overwrite. Used to drop 3 starter video commissions right in your area.
          </p>
        </div>
        {/* Math captcha — anti-bot defense. Auto-verifies on blur if
            the user typed an answer; also lazy-verifies when Submit is
            clicked. Self-hosted, no third-party tracker. */}
        <div data-testid="captcha-block">
          <Label htmlFor="captcha-answer" className="text-gray-300">Quick math check <span className="text-amber-400">*</span></Label>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-emerald-300 font-mono text-sm whitespace-nowrap" data-testid="captcha-question">
              {captchaQuestion || "Loading…"}
            </span>
            <Input
              id="captcha-answer"
              type="number"
              inputMode="numeric"
              value={captchaAnswer}
              onChange={(e) => { setCaptchaAnswer(e.target.value); setCaptchaVerified(false); setCaptchaError(""); }}
              onBlur={() => { if (captchaAnswer) verifyCaptcha(); }}
              className="bg-gray-800 border-gray-700 text-white max-w-[120px]"
              placeholder="?"
              required
              data-testid="captcha-input"
            />
            {captchaVerified && (
              <span className="text-emerald-400 text-xs font-semibold" data-testid="captcha-verified">✓ verified</span>
            )}
          </div>
          {captchaError && (
            <p className="text-amber-400 text-xs mt-1" data-testid="captcha-error">{captchaError}</p>
          )}
        </div>
        {error && <p className="text-red-400 text-sm" data-testid="auth-error">{error}</p>}
        <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={loading} data-testid="auth-submit">
          {loading ? "Loading..." : "Create Account"}
        </Button>
      </form>
    );
  }

// MagicLinkSentScreen — broken out so its hooks live above any
// conditional returns. Shows a 60s "resend" countdown, a spam-folder
// hint at 45s, and a fallback CTA at 60s so users aren't stranded
// when Resend's delivery is slow.
const MagicLinkSentScreen = ({ email, onResend, onBack, onTrySomethingElse }) => {
  const [secondsLeft, setSecondsLeft] = React.useState(60);
  const [resending, setResending] = React.useState(false);

  React.useEffect(() => {
    if (secondsLeft <= 0) return undefined;
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft]);

  const handleResend = async () => {
    setResending(true);
    try {
      await onResend();
      setSecondsLeft(60);
      toast.success("New link sent — check your inbox.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't resend. Try again in a moment.");
    }
    setResending(false);
  };

  return (
    <div className="space-y-3 text-center py-2" data-testid="magic-link-sent">
      <div className="flex justify-center">
        <div className="w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
          <CheckCircle2 className="h-7 w-7 text-emerald-400" />
        </div>
      </div>
      <h3 className="text-white text-lg font-semibold">Check your inbox</h3>
      <p className="text-gray-400 text-sm leading-relaxed">
        We sent a one-tap sign-in link to<br />
        <span className="text-emerald-400 font-medium">{email}</span>
      </p>
      <p className="text-gray-500 text-xs">
        The link expires in 15 minutes and can only be used once.
      </p>

      {secondsLeft >= 15 && (
        <p className="text-gray-500 text-[11px] italic">
          Not arriving in 15 seconds? Check your <span className="text-emerald-300">Spam / Junk</span> folder.
        </p>
      )}

      <Button
        type="button"
        onClick={handleResend}
        disabled={secondsLeft > 0 || resending}
        className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60"
        data-testid="magic-link-resend-btn"
      >
        {resending
          ? "Resending…"
          : secondsLeft > 0
            ? `Resend in ${secondsLeft}s`
            : "Resend sign-in link"}
      </Button>

      <Button
        type="button"
        variant="outline"
        onClick={onTrySomethingElse}
        className="w-full border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
        data-testid="magic-link-try-other-btn"
      >
        Didn't get it? Try another way →
      </Button>

      <button
        type="button"
        onClick={onBack}
        className="block w-full text-center text-xs text-gray-500 hover:text-gray-300 transition-colors pt-1"
        data-testid="magic-link-back-btn"
      >
        <ArrowLeft className="inline h-3 w-3 mr-1" /> Use a different email
      </button>
    </div>
  );
};

  // "magic-sent" mode — the listener is wired at the top of the component
  // so this section is purely render-only.
  if (mode === "magic-sent") {
    return (
      <MagicLinkSentScreen
        email={normalizedEmail()}
        onResend={() => requestMagicLink(normalizedEmail())}
        onBack={() => { setMode("magic"); setError(""); }}
        onTrySomethingElse={() => { setMode("password"); setError(""); }}
      />
    );
  }

  // ----- Login: SMS-OTP flow (Phase 4). Lifted into its own component
  // so the OTP step state doesn't leak into the magic-link/password
  // branches when the user toggles between modes.
  if (mode === "sms") {
    return (
      <SMSSignInForm
        onSuccess={onSuccess}
        onBack={() => { setMode("magic"); setError(""); }}
      />
    );
  }

  // ----- Login: magic-link form (primary path).
  if (mode === "magic") {
    return (
      <form onSubmit={handleMagicSubmit} className="space-y-4" data-testid="auth-form-magic">
        <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-3 flex items-start gap-2.5">
          <Wand2 className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
          <div className="text-xs text-gray-300 leading-relaxed">
            <span className="font-semibold text-emerald-400">No password needed.</span>{" "}
            We'll email you a one-tap sign-in link.
          </div>
        </div>
        <div>
          <Label htmlFor="email" className="text-gray-300">Email</Label>
          <Input
            id="email" type="email" value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bg-gray-800 border-gray-700 text-white mt-1"
            placeholder="you@example.com"
            required
            autoCapitalize="none" autoCorrect="off" spellCheck={false} inputMode="email"
            data-testid="email-input"
          />
        </div>
        {error && <p className="text-red-400 text-sm" data-testid="auth-error">{error}</p>}
        <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={loading} data-testid="magic-link-submit">
          <Mail className="h-4 w-4 mr-2" />
          {loading ? "Sending..." : "Send me a sign-in link"}
        </Button>

        {/* "OR" divider + Google button — the fast path. Google is given
            equal visual weight to the magic link so users with a Google
            account can skip the email round-trip entirely. */}
        <div className="flex items-center gap-2 my-1" aria-hidden="true">
          <div className="h-px bg-gray-700 flex-1" />
          <span className="text-[10px] uppercase tracking-widest text-gray-500">or</span>
          <div className="h-px bg-gray-700 flex-1" />
        </div>
        <GoogleAuthButton />
        <AppleAuthButton />
        <XAuthButton />

        <button
          type="button"
          onClick={() => { setMode("password"); setError(""); }}
          className="block w-full text-center text-xs text-gray-500 hover:text-gray-300 transition-colors pt-1"
          data-testid="auth-switch-to-password"
        >
          Use my password instead
        </button>
        <button
          type="button"
          onClick={() => { setMode("sms"); setError(""); }}
          className="flex items-center justify-center gap-1.5 w-full text-center text-xs text-gray-500 hover:text-emerald-400 transition-colors pt-1"
          data-testid="auth-switch-to-sms"
        >
          <Smartphone className="h-3.5 w-3.5" /> Sign in by SMS
        </button>
        <Link
          to="/qr-signin"
          className="flex items-center justify-center gap-1.5 w-full text-center text-xs text-gray-500 hover:text-emerald-400 transition-colors pt-1"
          data-testid="auth-switch-to-qr"
        >
          <QrCode className="h-3.5 w-3.5" /> Sign in with phone QR
        </Link>
      </form>
    );
  }

  // ----- Login: password fallback.
  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form">
      <div>
        <Label htmlFor="email" className="text-gray-300">Email</Label>
        <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          className="bg-gray-800 border-gray-700 text-white mt-1" placeholder="you@example.com"
          required
          autoCapitalize="none" autoCorrect="off" spellCheck={false} inputMode="email"
          data-testid="email-input" />
      </div>
      <div>
        <Label htmlFor="password" className="text-gray-300">Password</Label>
        <div className="relative mt-1">
          <Input
            id="password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="bg-gray-800 border-gray-700 text-white pr-10"
            placeholder="••••••••"
            required
            autoCapitalize="none" autoCorrect="off" spellCheck={false}
            data-testid="password-input"
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? "Hide password" : "Show password"}
            className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-emerald-400 transition-colors"
            data-testid="password-visibility-toggle"
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        <div className="mt-2 text-right">
          <Link
            to="/forgot-password"
            className="text-xs text-emerald-400 hover:text-emerald-300"
            data-testid="forgot-password-link"
          >
            Forgot password?
          </Link>
        </div>
      </div>
      {error && <p className="text-red-400 text-sm" data-testid="auth-error">{error}</p>}
      <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={loading} data-testid="auth-submit">
        {loading ? "Loading..." : "Sign In"}
      </Button>
      <button
        type="button"
        onClick={() => { setMode("magic"); setError(""); }}
        className="block w-full text-center text-xs text-gray-500 hover:text-emerald-400 transition-colors pt-1"
        data-testid="auth-switch-to-magic"
      >
        ← Email me a link instead (no password)
      </button>
    </form>
  );
};

export default AuthForm;
